import React, {useEffect, useRef, useState} from 'react'

import {Translator} from './i18n.js'
import {OTHER_SAFE_PLACE, TrackTraceAction, TrackTraceActionKey, TrackTraceActionRequest, TrackTraceActions} from '../../types/trackTrace.js'

export type ActionError = 'notAllowed' | 'invalid' | 'incompleteLink' | 'tooManyAttempts' | 'notFound' | 'error'

type Panel = TrackTraceActionKey

interface Props {
    tr: Translator
    actions: TrackTraceActions
    resellerName: string
    // Resolves with an error code, or null when the change was saved
    onAction: (request: TrackTraceActionRequest) => Promise<ActionError | null>
    // Opened right away, e.g. from a tile in a mail; ignored when the option is not available
    initialPanel?: Panel | null
}

function capitalized(text: string) {
    return text.charAt(0).toUpperCase() + text.slice(1)
}

export function placeLabel(place: string, tr: Translator) {
    return place === OTHER_SAFE_PLACE ? tr.t('safePlaceOther') : place
}

function blockedText(action: TrackTraceAction, tr: Translator) {
    switch (action.blockedBy) {
        case 'notAtDepot':
            return tr.t('blockedNotAtDepot')
        case 'notPossible':
            return tr.t('blockedNotPossible')
        default:
            return tr.t('blockedClosed')
    }
}

function Option({checked, label, sub, onSelect}: {checked: boolean, label: string, sub?: string, onSelect: () => void}) {
    return (
        <button type='button' role='radio' aria-checked={checked} className='tt-option' onClick={onSelect}>
            <i className={`mdi ${checked ? 'mdi-check-circle' : 'mdi-radiobox-blank'}`} aria-hidden='true' />
            <span className='tt-option-text'>
                <span className='tt-title-small'>{label}</span>
                {sub && <span className='tt-body-small' style={{whiteSpace: 'pre-line'}}>{sub}</span>}
            </span>
        </button>
    )
}

export default function ActionsCard({tr, actions, resellerName, onAction, initialPanel}: Props) {
    const {t} = tr
    const choice = actions.choice

    const [open, setOpen] = useState<Panel | null>(initialPanel && actions[initialPanel]?.available && choice?.type !== 'pickupPoint' ? initialPanel : null)
    const [place, setPlace] = useState(choice?.type === 'safePlace' ? choice.place : '')
    const [note, setNote] = useState(choice?.type === 'safePlace' ? choice.note : '')
    const [accepted, setAccepted] = useState(false)
    const [nr, setNr] = useState(choice?.type === 'neighbour' ? choice.nr : '')
    const [pickupPointId, setPickupPointId] = useState('')
    const [deliveryDay, setDeliveryDay] = useState('')
    const [instructions, setInstructions] = useState(actions.instructionsText)
    const [busy, setBusy] = useState(false)
    const [feedback, setFeedback] = useState<{type: 'saved' | 'error', text: string} | null>(null)
    const sectionRef = useRef<HTMLElement | null>(null)

    // An option opened from a mail is scrolled into view
    useEffect(() => {
        if (open && open === initialPanel) {
            sectionRef.current?.scrollIntoView?.({block: 'start'})
        }
    }, [])

    const toggle = (panel: Panel) => {
        setOpen(open === panel ? null : panel)
        setFeedback(null)
    }

    // Wijzigen: the chosen option opens with its current values
    const change = (panel: Panel) => {
        if (choice?.type === 'safePlace') {
            setPlace(choice.place)
            setNote(choice.note)
        } else if (choice?.type === 'neighbour') {
            setNr(choice.nr)
        }

        setDeliveryDay('')
        setInstructions(actions.instructionsText)
        setAccepted(false)
        setOpen(panel)
        setFeedback(null)
    }

    const submit = async (request: TrackTraceActionRequest) => {
        setBusy(true)
        setFeedback(null)

        const error = await onAction(request)

        setBusy(false)

        if (error) {
            setFeedback({type: 'error', text: error === 'notAllowed' ? t('actionNotAllowed') : error === 'invalid' ? t('actionInvalid') : t('actionError')})
        } else {
            setOpen(null)
            setAccepted(false)
            setFeedback({type: 'saved', text: t('saved')})
        }
    }

    const tiles: {panel: Panel, icon: string, short: string, long: string, action: TrackTraceAction | null}[] = [
        {panel: 'safePlace', icon: 'home-outline', short: t('tileSafePlace'), long: t('safePlaceTitle'), action: actions.safePlace},
        {panel: 'neighbour', icon: 'account-multiple', short: t('tileNeighbour'), long: t('neighbourTitle'), action: actions.neighbour},
        {panel: 'pickupPoint', icon: 'store-marker-outline', short: t('tilePickupPoint'), long: t('pickupPointTitle', {name: resellerName}), action: actions.pickupPoint},
        {panel: 'instructions', icon: 'message-text-outline', short: t('tileInstructions'), long: t('instructionsTitle'), action: actions.instructions},
        {panel: 'deliveryDay', icon: 'calendar-refresh', short: t('tileDeliveryDay'), long: t('deliveryDayTitle'), action: actions.deliveryDay}
    ]

    const options = [...actions.safePlaceOptions, OTHER_SAFE_PLACE]
    const safePlaceReady = !!place && accepted && (place !== OTHER_SAFE_PLACE || !!note.trim())
    const pickupPoint = actions.pickupPoints.find((point) => point.id === pickupPointId)

    const choiceText = choice?.type === 'safePlace' ?
        t('choiceSafePlace', {place: placeLabel(choice.place, tr)}) :
        choice?.type === 'neighbour' ?
            t('choiceNeighbour', {nr: choice.nr}) :
            choice?.type === 'deliveryDay' ? t('choiceDeliveryDay', {date: tr.date(choice.date)}) : ''
    const choiceAction = choice?.type === 'safePlace' ? actions.safePlace : choice?.type === 'neighbour' ? actions.neighbour : choice?.type === 'deliveryDay' ? actions.deliveryDay : null
    const chosenPanel: Panel | null = choice?.type === 'safePlace' || choice?.type === 'neighbour' || choice?.type === 'deliveryDay' ? choice.type : null

    // The parcel waits at a pickup point: show where, nothing else can be changed
    if (choice?.type === 'pickupPoint') {
        const {pickupPoint: point, until} = choice

        return (
            <section className='tt-card tt-actions' aria-labelledby='tt-actions-title'>
                <h2 id='tt-actions-title' className='tt-title-medium'>{t('choicePickupPoint', {name: point.name})}</h2>
                <div className='tt-choice'>
                    <i className='mdi mdi-store-marker-outline' aria-hidden='true' />
                    <div className='tt-row-text'>
                        <span className='tt-value'>{t(point.address ? 'pickupPointChosen' : 'pickupPointChosenNoAddress', {name: point.name, address: point.address, date: tr.date(until)})}</span>
                        {point.openingHours &&
                            <>
                                <span className='tt-body-small' style={{marginTop: 6}}>{t('openingHours')}</span>
                                <span style={{whiteSpace: 'pre-line'}}>{point.openingHours}</span>
                            </>
                        }
                    </div>
                </div>
            </section>
        )
    }

    return (
        <section ref={sectionRef} className='tt-card tt-actions' aria-labelledby='tt-actions-title'>
            <div className='tt-row-text' style={{gap: 3}}>
                <h2 id='tt-actions-title' className='tt-title-medium'>{t('actionsTitle')}</h2>
                <p className='tt-actions-intro'>{t('actionsIntro')}</p>
            </div>

            {chosenPanel && !open &&
                <div className='tt-choice'>
                    <i className='mdi mdi-check-circle' aria-hidden='true' />
                    <div className='tt-row-text'>
                        <span className='tt-body-small'>{t('yourChoice')}</span>
                        <span className='tt-value'>{choiceText}</span>
                        {choice.type === 'safePlace' && choice.note && <span>{choice.note}</span>}
                        {choiceAction?.available &&
                            <>
                                <span className='tt-body-small tt-choice-hint'>{t(chosenPanel === 'deliveryDay' ? 'choiceDeliveryDayHint' : 'choiceChangeHint')}</span>
                                <div className='tt-choice-buttons'>
                                    <button type='button' className='tt-text-button' disabled={busy} onClick={() => change(chosenPanel)}>{t('change')}</button>
                                    {/* Another delivery day already moved the order, choosing again is the way back */}
                                    {chosenPanel !== 'deliveryDay' &&
                                        <button type='button' className='tt-text-button' disabled={busy} onClick={() => submit({type: 'clearChoice'})}>{t('withdraw')}</button>
                                    }
                                </div>
                            </>
                        }
                    </div>
                </div>
            }

            {actions.instructionsText && !open &&
                <div className='tt-choice'>
                    <i className='mdi mdi-message-text-outline' aria-hidden='true' />
                    <div className='tt-row-text'>
                        <span className='tt-body-small'>{t('yourInstructions')}</span>
                        <span className='tt-value'>{actions.instructionsText}</span>
                        {actions.instructions?.available &&
                            <div className='tt-choice-buttons'>
                                <button type='button' className='tt-text-button' disabled={busy} onClick={() => change('instructions')}>{t('change')}</button>
                                <button type='button' className='tt-text-button' disabled={busy} onClick={() => submit({type: 'instructions', text: ''})}>{t('withdraw')}</button>
                            </div>
                        }
                    </div>
                </div>
            }

            <div className='tt-tiles'>
                {tiles.filter((tile) => tile.action).map(({panel, icon, short, long, action}) => (
                    <button
                        key={panel}
                        type='button'
                        className='tt-tile'
                        disabled={!action.available || busy}
                        aria-expanded={open === panel}
                        aria-controls='tt-action-panel'
                        title={action.available ? undefined : blockedText(action, tr)}
                        // Grey while another option is open, or when the option is no longer possible
                        data-inactive={!action.available || (!!open && open !== panel)}
                        data-chosen={panel === chosenPanel || (panel === 'instructions' && !!actions.instructionsText)}
                        onClick={() => toggle(panel)}
                    >
                        <span className='tt-tile-icon'><i className={`mdi mdi-${icon}`} aria-hidden='true' /></span>
                        <span className='tt-tile-short'>{short}</span>
                        <span className='tt-tile-long'>{long}</span>
                    </button>
                ))}
            </div>

            {feedback &&
                <p role={feedback.type === 'error' ? 'alert' : 'status'} className={feedback.type === 'error' ? 'tt-feedback tt-feedback-error' : 'tt-feedback'}>{feedback.text}</p>
            }

            {!open && tiles.some((tile) => tile.action && !tile.action.available) &&
                <ul className='tt-list tt-body-small'>
                    {tiles.filter((tile) => tile.action && !tile.action.available).map(({panel, short, action}) => (
                        <li key={panel}><span><strong>{short}</strong>: {blockedText(action, tr)}</span></li>
                    ))}
                </ul>
            }

            {open &&
                <div id='tt-action-panel' className='tt-panel'>
                    {open === 'safePlace' &&
                        <>
                            <h3 className='tt-title-medium'>{t('safePlaceQuestion')}</h3>
                            <div className='tt-options' role='radiogroup' aria-label={t('safePlaceQuestion')}>
                                {options.map((option) => <Option key={option} checked={place === option} label={placeLabel(option, tr)} onSelect={() => setPlace(option)} />)}
                            </div>
                            <div className='tt-field'>
                                <label htmlFor='tt-safe-note'>{place === OTHER_SAFE_PLACE ? t('safePlaceNote') : t('safePlaceNoteOptional')}</label>
                                <textarea id='tt-safe-note' className='tt-input tt-textarea' rows={2} maxLength={200} placeholder={t('safePlaceNotePlaceholder')} value={note} onChange={(event) => setNote(event.target.value)} />
                            </div>
                            <div className='tt-check'>
                                <input id='tt-safe-accept' type='checkbox' checked={accepted} onChange={(event) => setAccepted(event.target.checked)} />
                                <label htmlFor='tt-safe-accept'>{t('safePlaceDisclaimer')}</label>
                            </div>
                            <button type='button' className='tt-button tt-button-wide' disabled={!safePlaceReady || busy} onClick={() => submit({type: 'safePlace', place, note: note.trim(), accepted})}>
                                {t('confirm')}
                            </button>
                        </>
                    }

                    {open === 'neighbour' &&
                        <>
                            <div className='tt-field'>
                                <label htmlFor='tt-neighbour'>{t('neighbourLabel')}</label>
                                <input id='tt-neighbour' className='tt-input' maxLength={20} placeholder={t('neighbourPlaceholder')} value={nr} onChange={(event) => setNr(event.target.value)} />
                            </div>
                            <button type='button' className='tt-button tt-button-wide' disabled={!/^[0-9]/.test(nr.trim()) || busy} onClick={() => submit({type: 'neighbour', nr: nr.trim()})}>
                                {t('confirm')}
                            </button>
                        </>
                    }

                    {open === 'pickupPoint' &&
                        <>
                            <h3 className='tt-title-medium'>{t('pickupPointQuestion')}</h3>
                            <div className='tt-options' role='radiogroup' aria-label={t('pickupPointQuestion')}>
                                {actions.pickupPoints.map((point) => (
                                    <Option
                                        key={point.id}
                                        checked={pickupPointId === point.id}
                                        label={point.name}
                                        sub={[point.address, point.openingHours].filter(Boolean).join('\n')}
                                        onSelect={() => setPickupPointId(point.id)}
                                    />
                                ))}
                            </div>
                            <p className='tt-notice' role='note'>
                                <i className='mdi mdi-information-outline' aria-hidden='true' />
                                <span>{t('pickupPointNotice', {date: tr.date(actions.pickupUntil)})}</span>
                            </p>
                            <button type='button' className='tt-button tt-button-wide' disabled={!pickupPoint || busy} onClick={() => submit({type: 'pickupPoint', id: pickupPointId})}>
                                {t('confirm')}
                            </button>
                        </>
                    }

                    {open === 'deliveryDay' &&
                        <>
                            <h3 className='tt-title-medium'>{t('deliveryDayQuestion')}</h3>
                            <div className='tt-options' role='radiogroup' aria-label={t('deliveryDayQuestion')}>
                                {actions.deliveryDays.map((day) => <Option key={day} checked={deliveryDay === day} label={tr.relativeDate(day)} sub={tr.relativeDate(day) === capitalized(tr.date(day)) ? undefined : capitalized(tr.date(day))} onSelect={() => setDeliveryDay(day)} />)}
                            </div>
                            <button type='button' className='tt-button tt-button-wide' disabled={!actions.deliveryDays.includes(deliveryDay) || busy} onClick={() => submit({type: 'deliveryDay', date: deliveryDay})}>
                                {t('confirm')}
                            </button>
                        </>
                    }

                    {open === 'instructions' &&
                        <>
                            <div className='tt-field'>
                                <label htmlFor='tt-instructions'>{t('instructionsLabel')}</label>
                                <textarea id='tt-instructions' className='tt-input tt-textarea' rows={3} maxLength={200} placeholder={t('instructionsPlaceholder')} value={instructions} onChange={(event) => setInstructions(event.target.value)} />
                            </div>
                            <button type='button' className='tt-button tt-button-wide' disabled={busy || instructions.trim() === actions.instructionsText} onClick={() => submit({type: 'instructions', text: instructions.trim()})}>
                                {t('confirm')}
                            </button>
                        </>
                    }

                    <button type='button' className='tt-button tt-button-white tt-button-wide' onClick={() => toggle(open)}>{t('backToOptions')}</button>
                </div>
            }
        </section>
    )
}
